/**
 * Shared frontend file upload service.
 * Centralizes auth + workspace header injection, URL normalization, and error handling.
 */

// Error codes matching backend FileStorageErrorCode
export const FileUploadErrorCode = {
  FILE_TOO_LARGE: 'FILE_TOO_LARGE',
  INVALID_MIME_TYPE: 'INVALID_MIME_TYPE',
  MISSING_FILE: 'MISSING_FILE',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  UPLOAD_FAILED: 'UPLOAD_FAILED',
  NETWORK_ERROR: 'NETWORK_ERROR',
  /** A free organization past its storage quota (storage-quota-and-fair-use-plan.md §4.3). */
  STORAGE_FULL: 'STORAGE_FULL',
  /** File storage could not be reached or read (503) — try again later; not a problem with the file. */
  STORAGE_UNAVAILABLE: 'STORAGE_UNAVAILABLE',
} as const;

export type FileUploadErrorCodeType = typeof FileUploadErrorCode[keyof typeof FileUploadErrorCode];

export interface FileUploadError {
  code: FileUploadErrorCodeType;
  message: string;
}

export interface FileUploadResult {
  url: string;
  filename: string;
  size: number;
  mimeType: string;
}

export interface FileUploadSuccessResponse {
  success: true;
  data: FileUploadResult;
}

export interface FileUploadErrorResponse {
  success: false;
  error: FileUploadError;
}

export type FileUploadResponse = FileUploadSuccessResponse | FileUploadErrorResponse;

export interface FileUploadServiceConfig {
  /** Base URL for API calls (e.g. 'http://localhost:3002' or '') */
  baseUrl?: string;
  /**
   * Base URL for normalizing file serving URLs.
   * Use this when the API base URL contains a workspace slug (e.g. '/customer-support/helpdesk')
   * but file serving uses a static path (e.g. '/helpdesk').
   * If not provided, baseUrl is used for both API calls and file URL normalization.
   */
  fileBaseUrl?: string;
  /** Function to get the auth token */
  getAuthToken: () => string | null;
  /** Function to get current workspace ID (optional, for header injection) */
  getWorkspaceId?: () => string | null;
  /** Default upload endpoint. Defaults to '/api/files/upload' */
  uploadEndpoint?: string;
}

export interface UploadOptions {
  /** Category for the upload (e.g. 'covers', 'profiles') */
  category: string;
  /** Custom upload endpoint (overrides default) */
  endpoint?: string;
  /** Additional form data fields */
  additionalFields?: Record<string, string>;
  /** Abort signal for cancellation */
  signal?: AbortSignal;
}

/**
 * File upload service for frontend applications.
 * Handles auth headers, workspace context, and response normalization.
 */
export class FileUploadService {
  private config: Required<Omit<FileUploadServiceConfig, 'getWorkspaceId' | 'fileBaseUrl'>> & Pick<FileUploadServiceConfig, 'getWorkspaceId'> & { fileBaseUrl: string | undefined };

  constructor(config: FileUploadServiceConfig) {
    this.config = {
      baseUrl: config.baseUrl ?? '',
      fileBaseUrl: config.fileBaseUrl,
      getAuthToken: config.getAuthToken,
      getWorkspaceId: config.getWorkspaceId,
      uploadEndpoint: config.uploadEndpoint ?? '/api/files/upload',
    };
  }

  /**
   * Upload a file.
   * Returns canonical URL on success.
   */
  async upload(file: File, options: UploadOptions): Promise<FileUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', options.category);

    // Add any additional fields
    if (options.additionalFields) {
      for (const [key, value] of Object.entries(options.additionalFields)) {
        formData.append(key, value);
      }
    }

    const endpoint = options.endpoint ?? this.config.uploadEndpoint;
    const url = `${this.config.baseUrl}${endpoint}`;

    // Build headers
    const headers: Record<string, string> = {};
    
    const token = this.config.getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const workspaceId = this.config.getWorkspaceId?.();
    if (workspaceId) {
      headers['X-Workspace-Id'] = workspaceId;
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
        signal: options.signal,
      });

      const data = await response.json();

      // Handle error responses
      if (!response.ok || !data.success) {
        return {
          success: false,
          error: {
            code: data.error?.code ?? FileUploadErrorCode.UPLOAD_FAILED,
            message: data.error?.message ?? 'Upload failed',
          },
        };
      }

      // Normalize URL to be usable
      const normalizedUrl = this.normalizeUrl(data.data.url);

      return {
        success: true,
        data: {
          url: normalizedUrl,
          filename: data.data.filename,
          size: data.data.size,
          mimeType: data.data.mimeType,
        },
      };
    } catch (error) {
      // Handle network errors
      if (error instanceof Error && error.name === 'AbortError') {
        return {
          success: false,
          error: {
            code: FileUploadErrorCode.UPLOAD_FAILED,
            message: 'Upload cancelled',
          },
        };
      }

      return {
        success: false,
        error: {
          code: FileUploadErrorCode.NETWORK_ERROR,
          message: error instanceof Error ? error.message : 'Network error',
        },
      };
    }
  }

  /**
   * Upload an image file with validation.
   * Validates file type and size before upload.
   */
  async uploadImage(
    file: File, 
    options: UploadOptions & { maxSizeMB?: number }
  ): Promise<FileUploadResponse> {
    const maxSize = (options.maxSizeMB ?? 10) * 1024 * 1024;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      return {
        success: false,
        error: {
          code: FileUploadErrorCode.INVALID_MIME_TYPE,
          message: 'Please select an image file',
        },
      };
    }

    // Validate file size
    if (file.size > maxSize) {
      return {
        success: false,
        error: {
          code: FileUploadErrorCode.FILE_TOO_LARGE,
          message: `File size must be less than ${options.maxSizeMB ?? 10}MB`,
        },
      };
    }

    return this.upload(file, options);
  }

  /**
   * Normalize a URL to be usable in the current context.
   * - Relative URLs (/files/...) are made absolute using fileBaseUrl (or baseUrl as fallback)
   * - Already absolute URLs are returned as-is
   *
   * Uses fileBaseUrl (if configured) instead of baseUrl, because baseUrl may contain
   * a workspace slug (e.g. '/customer-support/helpdesk') needed for API routing,
   * while file serving uses a static path (e.g. '/helpdesk').
   */
  normalizeUrl(url: string): string {
    if (!url) return url;

    // Already absolute
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }

    // Use fileBaseUrl for file URL normalization, fall back to baseUrl
    const baseUrl = this.config.fileBaseUrl ?? (this.config.baseUrl || (typeof window !== 'undefined' ? window.location.origin : ''));
    
    // Ensure proper joining
    if (url.startsWith('/')) {
      return `${baseUrl}${url}`;
    }
    
    return `${baseUrl}/${url}`;
  }

  /**
   * Parse a canonical file URL into its path segments.
   * Accepts:  /files/{tenantId}/{workspaceId}/{category}/{filename}
   * Also handles absolute URLs by stripping the origin first.
   * Returns null if the URL doesn't match the canonical pattern.
   */
  parseFileUrl(url: string): { tenantId: string; workspaceId: string; category: string; filename: string } | null {
    if (!url) return null;
    // Strip origin if absolute
    let path = url;
    if (path.startsWith('http://') || path.startsWith('https://')) {
      try {
        path = new URL(path).pathname;
      } catch {
        return null;
      }
    }
    // Also strip any base path prefix (e.g. /helpdesk) that sits before /files/
    const filesIdx = path.indexOf('/files/');
    if (filesIdx === -1) return null;
    path = path.substring(filesIdx);

    const match = path.match(/^\/files\/([^/]+)\/([^/]+)\/([^/]+)\/([^/]+)$/);
    if (!match) return null;
    return { tenantId: match[1], workspaceId: match[2], category: match[3], filename: match[4] };
  }
}

